import React from 'react';
import { Stack, Typography, useTheme } from '@mui/material';
import { Description as DocumentIcon } from '@mui/icons-material';
import type { SxProps, Theme } from '@mui/material/styles';
import { useActiveDocumentContext } from '../../hooks/ActiveDocumentProvider';
import { layoutGap } from '../../../theme/theme';

export interface ActiveDocumentNameProps {
  compact?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Shows the active Revit document title (same source as the document segment in RuntimeModeBadge).
 */
const ActiveDocumentName: React.FC<ActiveDocumentNameProps> = ({ compact = false, sx }) => {
  const theme = useTheme();
  const documentName = useActiveDocumentContext();
  const sxOverrides = Array.isArray(sx) ? sx : sx ? [sx] : [];

  if (!documentName) {
    return null;
  }

  return (
    <Stack
      direction="row"
      spacing={layoutGap.dense}
      alignItems="center"
      sx={[
        {
          minWidth: 0,
          maxWidth: compact ? 200 : 280,
          flex: '0 1 auto',
        },
        ...sxOverrides,
      ]}
    >
      <DocumentIcon sx={{ fontSize: compact ? 12 : 14, color: theme.palette.info.main, flexShrink: 0 }} />
      <Typography
        variant="caption"
        data-testid="active-document-name"
        sx={{
          fontWeight: 600,
          fontSize: compact ? '0.62rem' : '0.68rem',
          lineHeight: compact ? 1.05 : 1.2,
          color: theme.palette.text.primary,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={documentName}
      >
        {documentName}
      </Typography>
    </Stack>
  );
};

export default ActiveDocumentName;
